import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from '../api';
import { useAuth } from '../context/AuthContext';
import './SuperuserTelegramSupport.css';

const POLL_MS = 2500;

function formatTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default function SuperuserTelegramSupport({ active }) {
  const { actor } = useAuth();
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [error, setError] = useState(null);
  const [sendBusy, setSendBusy] = useState(false);
  const [interceptBusy, setInterceptBusy] = useState(false);
  const listRef = useRef(null);

  const loadUsers = useCallback(async () => {
    setError(null);
    try {
      const res = await apiFetch('/api/admin/telegram-support/users/');
      if (res.status === 403) {
        setError('Нет доступа (нужен суперпользователь).');
        setUsers([]);
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Ошибка ${res.status}`);
      }
      const json = await res.json();
      const list = Array.isArray(json.users) ? json.users : [];
      setUsers(list);
    } catch (e) {
      setError(e.message || 'Ошибка загрузки');
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (users.length > 0 && selectedUserId == null) {
      setSelectedUserId(users[0].id);
    }
  }, [users, selectedUserId]);

  const selected = users.find((u) => u.id === selectedUserId) ?? null;

  const loadMessagesInitial = useCallback(async (userId, silent = false) => {
    if (userId == null) return;
    if (!silent) {
      setLoadingMessages(true);
      setError(null);
    }
    try {
      const res = await apiFetch(
        `/api/admin/telegram-support/messages/?user_id=${encodeURIComponent(userId)}&limit=500`
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Ошибка ${res.status}`);
      }
      const json = await res.json();
      const list = Array.isArray(json.messages) ? json.messages : [];
      setMessages(list);
    } catch (e) {
      if (!silent) {
        setError(e.message || 'Ошибка загрузки сообщений');
        setMessages([]);
      }
    } finally {
      if (!silent) setLoadingMessages(false);
    }
  }, []);

  useEffect(() => {
    if (selectedUserId == null) {
      setMessages([]);
      return;
    }
    loadMessagesInitial(selectedUserId);
  }, [selectedUserId, loadMessagesInitial]);

  const maxMessageId = messages.length
    ? Math.max(...messages.map((m) => m.id))
    : 0;

  const pollNew = useCallback(async () => {
    if (selectedUserId == null) return;
    try {
      const res = await apiFetch(
        `/api/admin/telegram-support/messages/?user_id=${encodeURIComponent(
          selectedUserId
        )}&after_id=${maxMessageId}&limit=500`
      );
      if (!res.ok) return;
      const json = await res.json();
      const incoming = Array.isArray(json.messages) ? json.messages : [];
      if (incoming.length === 0) return;
      setMessages((prev) => {
        const ids = new Set(prev.map((m) => m.id));
        const merged = [...prev];
        for (const m of incoming) {
          if (!ids.has(m.id)) merged.push(m);
        }
        merged.sort((a, b) => a.id - b.id);
        return merged;
      });
    } catch {
      /* ignore poll errors */
    }
  }, [selectedUserId, maxMessageId]);

  const reloadMessages = useCallback(() => {
    if (selectedUserId != null) loadMessagesInitial(selectedUserId, true);
  }, [selectedUserId, loadMessagesInitial]);

  useEffect(() => {
    if (!active || selectedUserId == null) return undefined;
    const tick = () => {
      if (maxMessageId <= 0) {
        reloadMessages();
      } else {
        pollNew();
      }
    };
    const t = setInterval(tick, POLL_MS);
    return () => clearInterval(t);
  }, [active, selectedUserId, maxMessageId, pollNew, reloadMessages]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, selectedUserId]);

  const onInterceptChange = async (e) => {
    if (!selected || interceptBusy) return;
    const next = e.target.checked;
    const other = selected.intercept_superuser_id != null && selected.intercept_superuser_id !== actor?.id;
    if (other && next) return;

    setInterceptBusy(true);
    setError(null);
    try {
      const res = await apiFetch('/api/admin/telegram-support/intercept/', {
        method: 'POST',
        body: JSON.stringify({ user_id: selected.id, intercept: next }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Ошибка ${res.status}`);
      await loadUsers();
    } catch (err) {
      setError(err.message || 'Ошибка переключения');
    } finally {
      setInterceptBusy(false);
    }
  };

  const send = async () => {
    const text = draft.trim();
    if (!selected || !text || sendBusy) return;
    setSendBusy(true);
    setError(null);
    try {
      const res = await apiFetch('/api/admin/telegram-support/send/', {
        method: 'POST',
        body: JSON.stringify({ user_id: selected.id, text }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Ошибка ${res.status}`);
      if (body.message) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === body.message.id)) return prev;
          return [...prev, body.message].sort((a, b) => a.id - b.id);
        });
      }
      setDraft('');
    } catch (err) {
      setError(err.message || 'Ошибка отправки');
    } finally {
      setSendBusy(false);
    }
  };

  if (loadingUsers) {
    return (
      <div className="tg-support tg-support--loading">
        <p>Загрузка…</p>
      </div>
    );
  }

  return (
    <div className="tg-support">
      <div className="tg-support-header">
        <h2 className="tg-support-title">Поддержка Telegram</h2>
        <button type="button" className="tg-support-refresh" onClick={loadUsers} disabled={interceptBusy}>
          Обновить список
        </button>
      </div>

      {error && <div className="tg-support-error">{error}</div>}

      {users.length === 0 ? (
        <p className="tg-support-empty">Нет пользователей с привязанным Telegram.</p>
      ) : (
        <>
          <div className="tg-support-toolbar">
            <label className="tg-support-label">
              Пользователь
              <select
                className="tg-support-select"
                value={selectedUserId ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setSelectedUserId(v === '' ? null : Number(v));
                }}
              >
                <option value="">— выберите —</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.username} (chat {u.telegram_chat_id})
                  </option>
                ))}
              </select>
            </label>

            {selected && (
              <label className="tg-support-intercept">
                <input
                  type="checkbox"
                  checked={selected.intercept_superuser_id === actor?.id}
                  disabled={
                    interceptBusy ||
                    (selected.intercept_superuser_id != null &&
                      selected.intercept_superuser_id !== actor?.id)
                  }
                  onChange={onInterceptChange}
                />
                <span>Переключить на меня</span>
                {selected.intercept_superuser_id != null &&
                  selected.intercept_superuser_id !== actor?.id && (
                    <>
                      <span className="tg-support-intercept-hint">
                        (перехват: {selected.intercept_superuser_username || `#${selected.intercept_superuser_id}`})
                      </span>
                      <button
                        type="button"
                        className="tg-support-clear-intercept"
                        disabled={interceptBusy}
                        onClick={async () => {
                          setInterceptBusy(true);
                          setError(null);
                          try {
                            const res = await apiFetch('/api/admin/telegram-support/intercept/', {
                              method: 'POST',
                              body: JSON.stringify({ user_id: selected.id, intercept: false }),
                            });
                            const body = await res.json().catch(() => ({}));
                            if (!res.ok) throw new Error(body.error || `Ошибка ${res.status}`);
                            await loadUsers();
                          } catch (err) {
                            setError(err.message || 'Ошибка');
                          } finally {
                            setInterceptBusy(false);
                          }
                        }}
                      >
                        Снять перехват
                      </button>
                    </>
                  )}
              </label>
            )}
          </div>

          {selected && (
            <>
              <div className="tg-support-meta">
                telegram_chat_id: <code>{selected.telegram_chat_id}</code>
              </div>
              <div className="tg-support-chat-wrap">
                {loadingMessages ? (
                  <div className="tg-support-chat-loading">Загрузка сообщений…</div>
                ) : (
                  <ul ref={listRef} className="tg-support-messages">
                    {messages.map((m) => (
                      <li
                        key={m.id}
                        className={`tg-support-msg tg-support-msg--${m.role} ${
                          m.role === 'assistant' && m.source === 'superuser' ? 'tg-support-msg--operator' : ''
                        }`}
                      >
                        <div className="tg-support-msg-head">
                          <span className="tg-support-msg-role">
                            {m.role === 'user'
                              ? 'Пользователь'
                              : m.source === 'superuser'
                                ? 'Бот (оператор)'
                                : 'Бот (агент)'}
                          </span>
                          <span className="tg-support-msg-time">{formatTime(m.created_at)}</span>
                        </div>
                        <div className="tg-support-msg-text">{m.text}</div>
                        {m.attachments?.length > 0 && (
                          <ul className="tg-support-msg-atts">
                            {m.attachments.map((a, i) => (
                              <li key={i}>
                                <a href={a.url} target="_blank" rel="noopener noreferrer">
                                  {a.attachment_type}: ссылка
                                </a>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="tg-support-compose">
                <textarea
                  className="tg-support-textarea"
                  rows={3}
                  placeholder="Сообщение от имени бота…"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  disabled={sendBusy}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                      e.preventDefault();
                      send();
                    }
                  }}
                />
                <button
                  type="button"
                  className="tg-support-send"
                  disabled={sendBusy || !draft.trim()}
                  onClick={send}
                >
                  {sendBusy ? 'Отправка…' : 'Отправить'}
                </button>
              </div>
              <p className="tg-support-hint">Ctrl+Enter — отправить</p>
            </>
          )}
        </>
      )}
    </div>
  );
}
